import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Pencil, Save, Search } from 'lucide-react'
import { DatePicker } from 'modules/permit/components/DatePicker'
import { searchAuditPermits } from 'modules/audit/api/permitAuditService'
import { getPermitDetail, type PermitDetail, type PermitListItem } from 'modules/permit/api/permitService'
import { PageTitle } from 'shared/components/layout/PageTitle'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Button } from 'shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Checkbox } from 'shared/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Textarea } from 'shared/ui/textarea'
import { getPersistedSelectedSiteId, resolveTenantCandidate, resolveTenantId } from 'shared/utils/tenantContext'
import {
  getTimesheetByPermitId,
  saveTimesheet,
  type TimesheetRecord,
} from '../api/timesheetService'

type LayoutState = {
  user?: any
  module?: 'E-PTW' | 'ADMIN'
  site?: {
    id?: string
    name?: string
  }
  menus?: any[]
  roles?: any[]
  tenantName?: string
}

type TimesheetFormState = {
  permitId: string
  permitNo: string
  remark: string
  time1Value: string
  time1AddDate: string
  time1AddByName: string
  time2Value: string
  time2AddDate: string
  time2AddByName: string
  time3Value: string
  time3AddDate: string
  time3AddByName: string
  time4Value: string
  time4AddDate: string
  time4AddByName: string
  time5Value: string
  time5AddDate: string
  time5AddByName: string
}

type SelectedPermitSummary = {
  permitId: string
  permitNo: string
  description: string
  plant: string
  unit: string
  company: string
  status: string
  workingDate: string
  permitTime: string
  permitType?: string | null
  plannedStart?: string | null
  plannedEnd?: string | null
}

const THAI_MORNING_SHIFT = '\u0e01\u0e30\u0e40\u0e0a\u0e49\u0e32'
const THAI_AFTERNOON_SHIFT = '\u0e01\u0e30\u0e1a\u0e48\u0e32\u0e22'
const THAI_START_WORK = '\u0e40\u0e23\u0e34\u0e48\u0e21\u0e07\u0e32\u0e19'
const THAI_LEAVE_AREA = '\u0e2d\u0e2d\u0e01\u0e08\u0e32\u0e01\u0e1e\u0e37\u0e49\u0e19\u0e17\u0e35\u0e48'
const THAI_RESUME_BEFORE_1400 = '\u0e40\u0e23\u0e34\u0e48\u0e21\u0e07\u0e32\u0e19\u0e01\u0e48\u0e2d\u0e19 14:00'
const THAI_COMMENT = '\u0e2b\u0e21\u0e32\u0e22\u0e40\u0e2b\u0e15\u0e38'

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const TIME_HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'))
const TIME_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'))

const createInitialForm = (permitId = '', permitNo = ''): TimesheetFormState => ({
  permitId,
  permitNo,
  remark: '',
  time1Value: '',
  time1AddDate: '',
  time1AddByName: '',
  time2Value: '',
  time2AddDate: '',
  time2AddByName: '',
  time3Value: '',
  time3AddDate: '',
  time3AddByName: '',
  time4Value: '',
  time4AddDate: '',
  time4AddByName: '',
  time5Value: '',
  time5AddDate: '',
  time5AddByName: '',
})

const normalizeGuid = (value?: string | null) => {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  if (!trimmed || !GUID_PATTERN.test(trimmed)) {
    return null
  }

  return trimmed.toLowerCase()
}

const normalizeText = (value?: string | null) => value?.trim() ?? ''

const resolveUserCompanyId = (user?: Record<string, any> | null) => {
  const candidates = [
    user?.companyId,
    user?.company_id,
    user?.organizationId,
    user?.organization_id,
    user?.company?.companyId,
    user?.company?.id,
  ]

  for (const candidate of candidates) {
    const normalized = normalizeGuid(candidate)
    if (normalized) {
      return normalized
    }
  }

  return null
}

const formatDateTime = (value?: string | null) => {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

const formatPermitDatePart = (value?: string | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const year = String(parsed.getFullYear())
  return `${day}-${month}-${year}`
}

const formatPermitTimePart = (value?: string | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const hours = String(parsed.getHours()).padStart(2, '0')
  const minutes = String(parsed.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

const formatPermitDateRange = (start?: string | null, end?: string | null) => {
  const startText = formatPermitDatePart(start)
  const endText = formatPermitDatePart(end)
  if (startText && endText) {
    return startText === endText ? startText : `${startText} - ${endText}`
  }
  return startText ?? endText ?? '-'
}

const formatPermitTimeRange = (start?: string | null, end?: string | null) => {
  const startText = formatPermitTimePart(start)
  const endText = formatPermitTimePart(end)
  if (startText && endText) {
    return `${startText} - ${endText}`
  }
  return startText ?? endText ?? '-'
}

const parsePermitLocation = (location?: string | null) => {
  const normalized = normalizeText(location)
  if (!normalized) {
    return { plant: '', unit: '' }
  }

  const separator = normalized.includes('/') ? '/' : normalized.includes(':') ? ':' : null
  if (!separator) {
    return { plant: normalized, unit: '' }
  }

  const [plant, unit] = normalized.split(separator).map((item) => normalizeText(item))
  return {
    plant,
    unit,
  }
}

const parseTimeParts = (value?: string | null) => {
  if (!value || typeof value !== 'string' || !value.includes(':')) {
    return { hour: '', minute: '' }
  }

  const [rawHour = '', rawMinute = ''] = value.split(':')
  return {
    hour: rawHour.padStart(2, '0').slice(-2),
    minute: rawMinute.padStart(2, '0').slice(-2),
  }
}

const mapPermitToSummary = (permit: PermitListItem): SelectedPermitSummary => {
  const parsedLocation = parsePermitLocation(permit.locationName ?? permit.location)
  return {
    permitId: permit.permitId,
    permitNo: permit.permitNumber,
    description: permit.workDescription || permit.title || '-',
    plant: permit.area || parsedLocation.plant || '-',
    unit: permit.unit || permit.unitName || parsedLocation.unit || '-',
    company: permit.requesterCompany || '-',
    status: permit.statusName || permit.status || '-',
    workingDate: formatPermitDateRange(permit.plannedStart, permit.plannedEnd),
    permitTime: formatPermitTimeRange(permit.plannedStart, permit.plannedEnd),
    permitType: permit.permitType ?? null,
    plannedStart: permit.plannedStart ?? null,
    plannedEnd: permit.plannedEnd ?? null,
  }
}

const mapPermitDetailToSummary = (detail: PermitDetail): SelectedPermitSummary => {
  // detail.metadata.locationName (e.g. "PLBG : SASB") is where the API actually
  // puts the resolved location - workLocation/location/area are kept as fallback
  // keys only, they aren't what's populated for plot-plan-based permits.
  const parsedLocation = parsePermitLocation(
    detail.metadata?.workLocation ?? detail.metadata?.location ?? detail.metadata?.locationName ?? detail.metadata?.area,
  )
  return {
    permitId: detail.permitId,
    permitNo: detail.permitNumber,
    description: detail.title || '-',
    plant: detail.metadata?.area || parsedLocation.plant || '-',
    unit: detail.metadata?.unit || parsedLocation.unit || '-',
    company: detail.requesterCompany || '-',
    status: detail.statusName || detail.status || '-',
    workingDate: formatPermitDateRange(detail.plannedStart, detail.plannedEnd),
    permitTime: formatPermitTimeRange(detail.plannedStart, detail.plannedEnd),
    permitType: detail.permitTypeName ?? null,
    plannedStart: detail.plannedStart ?? null,
    plannedEnd: detail.plannedEnd ?? null,
  }
}

const mapRecordToForm = (record: TimesheetRecord, permitNo: string): TimesheetFormState => ({
  permitId: record.permitId,
  permitNo,
  remark: normalizeText(record.remark),
  time1Value: normalizeText(record.time1Value),
  time1AddDate: formatDateTime(record.time1AddDate),
  time1AddByName: normalizeText(record.time1AddByName),
  time2Value: normalizeText(record.time2Value),
  time2AddDate: formatDateTime(record.time2AddDate),
  time2AddByName: normalizeText(record.time2AddByName),
  time3Value: normalizeText(record.time3Value),
  time3AddDate: formatDateTime(record.time3AddDate),
  time3AddByName: normalizeText(record.time3AddByName),
  time4Value: normalizeText(record.time4Value),
  time4AddDate: formatDateTime(record.time4AddDate),
  time4AddByName: normalizeText(record.time4AddByName),
  time5Value: normalizeText(record.time5Value),
  time5AddDate: formatDateTime(record.time5AddDate),
  time5AddByName: normalizeText(record.time5AddByName),
})

const isValidTimeValue = (value: string) => value.trim().length === 0 || TIME_PATTERN.test(value.trim())

export function TimesheetEditorPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams<{ permitId: string }>()
  const state = (location.state as LayoutState | undefined) ?? {}
  const routePermitId = useMemo(() => decodeURIComponent(params.permitId ?? ''), [params.permitId])
  const isViewMode = location.pathname.endsWith('/view')
  const isCreateMode = location.pathname.endsWith('/create')
  const persistedSiteId = getPersistedSelectedSiteId()
  const tenantId = resolveTenantId(state.site?.id, persistedSiteId)
  const siteId = resolveTenantCandidate(state.site?.id, persistedSiteId)
  const currentUserCompanyId = useMemo(() => resolveUserCompanyId(state.user), [state.user])
  const [form, setForm] = useState<TimesheetFormState>(() => createInitialForm(routePermitId))
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedPermit, setSelectedPermit] = useState<SelectedPermitSummary | null>(null)
  const [isPermitDialogOpen, setIsPermitDialogOpen] = useState(false)
  const [permitSearch, setPermitSearch] = useState('')
  const [permitDateFrom, setPermitDateFrom] = useState('')
  const [permitDateTo, setPermitDateTo] = useState('')
  const [permitSearchResults, setPermitSearchResults] = useState<SelectedPermitSummary[]>([])
  const [isPermitSearchLoading, setIsPermitSearchLoading] = useState(false)
  const [selectedDialogPermitId, setSelectedDialogPermitId] = useState('')

  useEffect(() => {
    if (!state.user) {
      navigate('/modules', { replace: true })
    }
  }, [navigate, state.user])

  const loadRecord = useCallback(async (permitId: string) => {
    const normalizedPermitId = permitId.trim()
    if (!normalizedPermitId) {
      setError('Permit is required before loading timesheet data.')
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const recordPromise = getTimesheetByPermitId(normalizedPermitId)

      let nextSelectedPermit: SelectedPermitSummary | null = null
      if (tenantId) {
        try {
          const detail = await getPermitDetail(tenantId, normalizedPermitId)
          if (detail) {
            nextSelectedPermit = mapPermitDetailToSummary(detail)
          }
        } catch {
          nextSelectedPermit = null
        }
      }
      setSelectedPermit(nextSelectedPermit)

      const record = await recordPromise
      if (record) {
        setForm(mapRecordToForm(record, nextSelectedPermit?.permitNo ?? ''))
      } else if (!isCreateMode) {
        setForm(createInitialForm(normalizedPermitId, nextSelectedPermit?.permitNo ?? ''))
        if (!nextSelectedPermit) {
          setError('Permit was not found.')
        }
      } else {
        setForm((prev) => ({ ...prev, permitId: normalizedPermitId, permitNo: nextSelectedPermit?.permitNo ?? prev.permitNo }))
      }
    } catch (err: any) {
      setError(err?.message ?? 'Unable to load timesheet data.')
    } finally {
      setIsLoading(false)
    }
  }, [isCreateMode, tenantId])

  useEffect(() => {
    if (!routePermitId || isCreateMode) {
      return
    }
    void loadRecord(routePermitId)
  }, [isCreateMode, loadRecord, routePermitId])

  useEffect(() => {
    if (!isPermitDialogOpen || !tenantId) {
      return
    }

    let isCancelled = false

    const loadPermits = async () => {
      setIsPermitSearchLoading(true)
      try {
        const response = await searchAuditPermits({
          tenantId,
          siteId,
          companyId: currentUserCompanyId ?? undefined,
          searchTerm: permitSearch.trim() || undefined,
          plannedStartFrom: permitDateFrom || undefined,
          plannedEndTo: permitDateTo || undefined,
          pageNumber: 1,
          pageSize: 10,
        })

        if (isCancelled) {
          return
        }

        setPermitSearchResults((response.items ?? []).map(mapPermitToSummary))
      } catch (err: any) {
        if (isCancelled) {
          return
        }
        setError(err?.message ?? 'Unable to search permits for timesheet creation.')
      } finally {
        if (!isCancelled) {
          setIsPermitSearchLoading(false)
        }
      }
    }

    void loadPermits()

    return () => {
      isCancelled = true
    }
  }, [currentUserCompanyId, isPermitDialogOpen, permitDateFrom, permitDateTo, permitSearch, siteId, tenantId])

  const validateForm = () => {
    if (!form.permitId.trim()) {
      return 'Permit is required.'
    }

    const values = [
      form.time1Value,
      form.time2Value,
      form.time3Value,
      form.time4Value,
      form.time5Value,
    ]

    if (values.some((value) => !isValidTimeValue(value))) {
      return 'Time values must use HH:MM format.'
    }

    return null
  }

  const handleSave = async () => {
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      await saveTimesheet({
        permitId: form.permitId.trim(),
        remark: form.remark.trim() || null,
        time1Value: form.time1Value.trim() || null,
        time2Value: form.time2Value.trim() || null,
        time3Value: form.time3Value.trim() || null,
        time4Value: form.time4Value.trim() || null,
        time5Value: form.time5Value.trim() || null,
      })
      navigate('/timesheet', { replace: true, state })
    } catch (err: any) {
      setError(err?.message ?? 'Unable to save timesheet.')
    } finally {
      setIsSaving(false)
    }
  }

  const updateField = (key: keyof TimesheetFormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const updateTimeField = useCallback(
    (
      fieldKey: 'time1Value' | 'time2Value' | 'time3Value' | 'time4Value' | 'time5Value',
      updates: { hour?: string; minute?: string },
    ) => {
      setForm((prev) => {
        const currentParts = parseTimeParts(prev[fieldKey])
        const nextHour = updates.hour ?? currentParts.hour
        const nextMinute = updates.minute ?? currentParts.minute

        if (!nextHour) {
          return { ...prev, [fieldKey]: '' }
        }

        return {
          ...prev,
          [fieldKey]: `${nextHour}:${nextMinute || '00'}`,
        }
      })
    },
    [],
  )

  const clearTimeField = useCallback(
    (fieldKey: 'time1Value' | 'time2Value' | 'time3Value' | 'time4Value' | 'time5Value') => {
      setForm((prev) => ({ ...prev, [fieldKey]: '' }))
    },
    [],
  )

  const handleOpenPermitDialog = () => {
    setSelectedDialogPermitId(selectedPermit?.permitId ?? '')
    setPermitSearch('')
    setPermitDateFrom('')
    setPermitDateTo('')
    setIsPermitDialogOpen(true)
  }

  const handleApplySelectedPermit = async () => {
    const matchedPermit = permitSearchResults.find((item) => item.permitId === selectedDialogPermitId)
    if (!matchedPermit) {
      setError('Please select a permit before applying.')
      return
    }

    setSelectedPermit(matchedPermit)
    setForm((prev) => ({ ...createInitialForm(matchedPermit.permitId, matchedPermit.permitNo), remark: prev.remark }))
    await loadRecord(matchedPermit.permitId)
    setIsPermitDialogOpen(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <PageTitle
            currentPage="timesheet"
            compact
            titleOverride={
              isViewMode ? 'View Permit Timesheet' : isCreateMode ? 'Create Permit Timesheet' : 'Edit Permit Timesheet'
            }
            descriptionOverride="Capture the morning and afternoon time checkpoints for the selected permit."
          />
        </div>
        <Button variant="outline" onClick={() => navigate('/timesheet', { state })}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to List
        </Button>
      </div>

      {isLoading && !isCreateMode ? (
        <Card>
          <CardContent className="flex items-center justify-center gap-2 py-16 text-slate-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading timesheet...</span>
          </CardContent>
        </Card>
      ) : (
      <>
      <Card>
        <CardHeader>
          <CardTitle>Permit Reference</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="w-full lg:max-w-sm">
              <Label htmlFor="permitNo">{isCreateMode ? 'Selected Permit No' : 'Permit No'}</Label>
              <Input
                id="permitNo"
                value={form.permitNo}
                onChange={(event) => updateField('permitNo', event.target.value)}
                readOnly={isCreateMode || isViewMode}
                disabled={!isCreateMode || isViewMode}
                placeholder={isCreateMode ? 'Select permit...' : 'Enter permit number'}
                className="mt-1"
              />
            </div>
            {isCreateMode && !isViewMode ? (
              <Button type="button" onClick={handleOpenPermitDialog} disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                Select Permit
              </Button>
            ) : null}
          </div>

          {selectedPermit ? (
            <div className="overflow-x-auto rounded-lg border">
              <table className="min-w-full text-sm">
                <thead className="[&_tr]:border-b">
                  <tr className="border-b bg-slate-50 transition-colors">
                    <th className="h-11 px-3 text-left align-middle font-medium text-slate-600">Permit No</th>
                    <th className="h-11 px-3 text-left align-middle font-medium text-slate-600">Working Date</th>
                    <th className="h-11 px-3 text-left align-middle font-medium text-slate-600">Permit Time</th>
                    <th className="h-11 px-3 text-left align-middle font-medium text-slate-600">Company</th>
                    <th className="h-11 px-3 text-left align-middle font-medium text-slate-600">Plant</th>
                    <th className="h-11 px-3 text-left align-middle font-medium text-slate-600">Unit</th>
                    <th className="h-11 px-3 text-left align-middle font-medium text-slate-600">Description</th>
                    <th className="h-11 px-3 text-left align-middle font-medium text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody className="[&_tr:last-child]:border-0">
                  <tr className="border-b">
                    <td className="p-3 align-top">{selectedPermit.permitNo}</td>
                    <td className="p-3 align-top">{selectedPermit.workingDate}</td>
                    <td className="p-3 align-top">{selectedPermit.permitTime}</td>
                    <td className="p-3 align-top">{selectedPermit.company}</td>
                    <td className="p-3 align-top">{selectedPermit.plant}</td>
                    <td className="p-3 align-top">{selectedPermit.unit}</td>
                    <td className="p-3 align-top">{selectedPermit.description}</td>
                    <td className="p-3 align-top">{selectedPermit.status}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{THAI_MORNING_SHIFT}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TimesheetTimeRow
            fieldKey="time1Value"
            label={THAI_START_WORK}
            value={form.time1Value}
            onHourChange={(value) => updateTimeField('time1Value', { hour: value })}
            onMinuteChange={(value) => updateTimeField('time1Value', { minute: value })}
            onClear={() => clearTimeField('time1Value')}
            addByName={form.time1AddByName}
            addDate={form.time1AddDate}
            readOnly={isViewMode || isLoading}
          />
          <TimesheetTimeRow
            fieldKey="time2Value"
            label={THAI_LEAVE_AREA}
            value={form.time2Value}
            onHourChange={(value) => updateTimeField('time2Value', { hour: value })}
            onMinuteChange={(value) => updateTimeField('time2Value', { minute: value })}
            onClear={() => clearTimeField('time2Value')}
            addByName={form.time2AddByName}
            addDate={form.time2AddDate}
            readOnly={isViewMode || isLoading}
          />
          <TimesheetTimeRow
            fieldKey="time3Value"
            label={THAI_RESUME_BEFORE_1400}
            value={form.time3Value}
            onHourChange={(value) => updateTimeField('time3Value', { hour: value })}
            onMinuteChange={(value) => updateTimeField('time3Value', { minute: value })}
            onClear={() => clearTimeField('time3Value')}
            addByName={form.time3AddByName}
            addDate={form.time3AddDate}
            readOnly={isViewMode || isLoading}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{THAI_AFTERNOON_SHIFT}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TimesheetTimeRow
            fieldKey="time4Value"
            label={THAI_START_WORK}
            value={form.time4Value}
            onHourChange={(value) => updateTimeField('time4Value', { hour: value })}
            onMinuteChange={(value) => updateTimeField('time4Value', { minute: value })}
            onClear={() => clearTimeField('time4Value')}
            addByName={form.time4AddByName}
            addDate={form.time4AddDate}
            readOnly={isViewMode || isLoading}
          />
          <TimesheetTimeRow
            fieldKey="time5Value"
            label={THAI_LEAVE_AREA}
            value={form.time5Value}
            onHourChange={(value) => updateTimeField('time5Value', { hour: value })}
            onMinuteChange={(value) => updateTimeField('time5Value', { minute: value })}
            onClear={() => clearTimeField('time5Value')}
            addByName={form.time5AddByName}
            addDate={form.time5AddDate}
            readOnly={isViewMode || isLoading}
          />

          <div className="grid gap-2 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-start">
            <Label htmlFor="remark" className="pt-2">
              {THAI_COMMENT}
            </Label>
            <Textarea
              id="remark"
              rows={4}
              value={form.remark}
              onChange={(event) => updateField('remark', event.target.value)}
              readOnly={isViewMode || isLoading}
              placeholder={THAI_COMMENT}
            />
          </div>
        </CardContent>
      </Card>

      {!isViewMode ? (
        <div className="sticky bottom-0 left-0 right-0 border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-white/75">
          <div className="flex w-full items-center gap-4 px-4 py-4">
            <div className="text-sm">
              {error ? (
                <span className="text-red-600">{error}</span>
              ) : (
                <span className="text-gray-500">Save to keep this permit timesheet record.</span>
              )}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/timesheet', { state })} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Timesheet
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      </>
      )}

      <Dialog open={isPermitDialogOpen} onOpenChange={setIsPermitDialogOpen}>
        <DialogContent className="flex max-h-[90vh] w-[min(96vw,1400px)] max-w-[96vw] flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 px-6 pt-6">
            <DialogTitle>Select Permit</DialogTitle>
            <DialogDescription>Choose a permit that should be used for this timesheet.</DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 px-6 pb-4">
            <div className="shrink-0 rounded-lg border bg-slate-50 px-4 py-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] flex-[1_1_320px]">
                  <Label htmlFor="permit-timesheet-search" className="mb-2 block text-sm font-semibold text-slate-900">
                    Permit No
                  </Label>
                  <Input
                    id="permit-timesheet-search"
                    value={permitSearch}
                    onChange={(event) => setPermitSearch(event.target.value)}
                    placeholder="Search by permit number or work description"
                  />
                </div>

                <div className="min-w-[160px] flex-[0_1_180px]">
                  <Label htmlFor="permit-timesheet-date-from" className="mb-2 block text-sm font-semibold text-slate-900">
                    Working Date
                  </Label>
                  <DatePicker
                    id="permit-timesheet-date-from"
                    value={permitDateFrom}
                    onChange={setPermitDateFrom}
                    placeholder="Select date..."
                    className="h-11"
                    allowPastDates
                  />
                </div>

                <div className="min-w-[160px] flex-[0_1_180px]">
                  <Label htmlFor="permit-timesheet-date-to" className="mb-2 block text-sm font-semibold text-slate-900">
                    To
                  </Label>
                  <DatePicker
                    id="permit-timesheet-date-to"
                    value={permitDateTo}
                    onChange={setPermitDateTo}
                    placeholder="Select date..."
                    className="h-11"
                    allowPastDates
                  />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPermitSearch('')
                    setPermitDateFrom('')
                    setPermitDateTo('')
                  }}
                  className="h-11 shrink-0 justify-center"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>

            <div className="min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg border bg-white">
              <div className="h-full min-w-0 overflow-x-auto">
                <div className="max-h-full overflow-y-auto">
                  <table className="min-w-[1650px] caption-bottom text-sm">
                    <thead className="[&_tr]:border-b">
                      <tr className="border-b transition-colors hover:bg-muted/50">
                        <th className="h-12 min-w-[64px] px-4 text-left align-middle font-medium text-muted-foreground">Select</th>
                        <th className="h-12 min-w-[120px] px-4 text-left align-middle font-medium text-muted-foreground">Permit No</th>
                        <th className="h-12 min-w-[120px] px-4 text-left align-middle font-medium text-muted-foreground">Working Date</th>
                        <th className="h-12 min-w-[120px] px-4 text-left align-middle font-medium text-muted-foreground">Permit Time</th>
                        <th className="h-12 min-w-[260px] px-4 text-left align-middle font-medium text-muted-foreground">Company</th>
                        <th className="h-12 min-w-[160px] px-4 text-left align-middle font-medium text-muted-foreground">Plant</th>
                        <th className="h-12 min-w-[120px] px-4 text-left align-middle font-medium text-muted-foreground">Unit</th>
                        <th className="h-12 min-w-[180px] px-4 text-left align-middle font-medium text-muted-foreground">Permit Type</th>
                        <th className="h-12 min-w-[460px] px-4 text-left align-middle font-medium text-muted-foreground">Description</th>
                        <th className="h-12 min-w-[220px] px-4 text-left align-middle font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody className="[&_tr:last-child]:border-0">
                      {isPermitSearchLoading ? (
                        <tr className="border-b transition-colors hover:bg-muted/50">
                          <td colSpan={10} className="p-4 text-center align-middle text-sm text-gray-500">
                            Loading permits...
                          </td>
                        </tr>
                      ) : permitSearchResults.length === 0 ? (
                        <tr className="border-b transition-colors hover:bg-muted/50">
                          <td colSpan={10} className="p-4 text-center align-middle text-sm text-gray-500">
                            No permits found.
                          </td>
                        </tr>
                      ) : (
                        permitSearchResults.map((permit) => (
                          <tr
                            key={permit.permitId}
                            className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                          >
                            <td className="p-4 align-middle">
                              <Checkbox
                                checked={selectedDialogPermitId === permit.permitId}
                                onCheckedChange={(checked) => {
                                  setSelectedDialogPermitId(checked === true ? permit.permitId : '')
                                }}
                              />
                            </td>
                            <td className="whitespace-nowrap p-4 align-middle font-medium">{permit.permitNo}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{permit.workingDate}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{permit.permitTime}</td>
                            <td className="p-4 align-middle">{permit.company || '-'}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{permit.plant || '-'}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{permit.unit || '-'}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{permit.permitType || '-'}</td>
                            <td className="p-4 align-middle">{permit.description || '-'}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{permit.status || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setIsPermitDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleApplySelectedPermit()}>Apply Selection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface TimesheetTimeRowProps {
  fieldKey: 'time1Value' | 'time2Value' | 'time3Value' | 'time4Value' | 'time5Value'
  label: string
  thaiLabel?: string
  value: string
  onHourChange: (value: string) => void
  onMinuteChange: (value: string) => void
  onClear: () => void
  addByName?: string
  addDate?: string
  readOnly?: boolean
}

function TimesheetTimeRow({
  fieldKey,
  label,
  thaiLabel,
  value,
  onHourChange,
  onMinuteChange,
  onClear,
  addByName,
  addDate,
  readOnly = false,
}: TimesheetTimeRowProps) {
  const hasInvalidTime = value.trim().length > 0 && !TIME_PATTERN.test(value.trim())
  const [hour = '', minute = ''] = value.split(':')

  return (
    <div className="grid gap-2 lg:grid-cols-[220px_220px_minmax(0,1fr)] lg:items-center">
      <div className="flex items-center justify-between gap-3">
        <Label className="flex items-center gap-2">
          <span>{label}</span>
          {thaiLabel ? <span className="text-red-500">{thaiLabel}</span> : null}
        </Label>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            className="h-auto p-0 text-xs text-gray-500 hover:text-gray-700"
            onClick={onClear}
            disabled={readOnly}
          >
            ล้างค่า
          </Button>
        ) : null}
      </div>
      <div>
        <div className="flex items-center gap-2">
          <Select value={hour || undefined} onValueChange={onHourChange} disabled={readOnly}>
            <SelectTrigger className="border-gray-200 bg-gray-50">
              <SelectValue placeholder="ชั่วโมง" />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              {TIME_HOUR_OPTIONS.map((option) => (
                <SelectItem key={`${fieldKey}-hour-${option}`} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-gray-400">:</span>
          <Select value={minute || undefined} onValueChange={onMinuteChange} disabled={readOnly || !hour}>
            <SelectTrigger className="border-gray-200 bg-gray-50">
              <SelectValue placeholder="นาที" />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              {TIME_MINUTE_OPTIONS.map((option) => (
                <SelectItem key={`${fieldKey}-minute-${option}`} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasInvalidTime ? <p className="mt-1 text-xs text-red-500">Use HH:MM format</p> : null}
      </div>
      <div className="text-sm text-rose-600">
        {addByName || addDate ? `[${addByName || '-'} : ${addDate || '-'}]` : ''}
      </div>
    </div>
  )
}
